/**
 * SoundBank — resolves a sound id to a decoded AudioBuffer, cached.
 *
 * Resolution order per id: if the manifest entry has a `url`, fetch + decode it;
 * on any failure (or no url) fall back to baking the procedural `synth` spec
 * offline. Either way the result is an AudioBuffer that downstream code plays
 * identically — files and synths are interchangeable.
 *
 * Loads are de-duplicated (one in-flight promise per id) and memoised. `get()`
 * is the hot-path accessor: it returns the buffer if ready, else kicks off the
 * load and returns null so the caller can simply skip a not-yet-ready one-shot.
 */
import { renderSynth } from "./synth";
import { defOf } from "./manifest";

export class SoundBank {
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<AudioBuffer | null>>();

  constructor(private ctx: AudioContext) {}

  /** Ready buffer or null (and start loading). Use on the play hot-path. */
  get(id: string): AudioBuffer | null {
    const b = this.buffers.get(id);
    if (b) return b;
    void this.load(id);
    return null;
  }

  has(id: string): boolean {
    return this.buffers.has(id);
  }

  /** Ensure an id is decoded/baked; resolves to its buffer (or null if unknown). */
  load(id: string): Promise<AudioBuffer | null> {
    const ready = this.buffers.get(id);
    if (ready) return Promise.resolve(ready);
    const inflight = this.loading.get(id);
    if (inflight) return inflight;

    const def = defOf(id);
    if (!def) {
      if (import.meta.env.DEV) console.warn(`[audio] unknown sound id "${id}"`);
      return Promise.resolve(null);
    }

    const p = (async (): Promise<AudioBuffer | null> => {
      // Prefer a real file when the manifest points at one.
      if (def.url) {
        try {
          const res = await fetch(def.url);
          if (res.ok) {
            const buf = await this.ctx.decodeAudioData(await res.arrayBuffer());
            this.buffers.set(id, buf);
            return buf;
          }
        } catch {
          /* fall through to the synth fallback */
        }
        if (import.meta.env.DEV) console.warn(`[audio] "${id}" file failed → synth fallback (${def.url})`);
      }
      // Procedural fallback — always available.
      const baked = await renderSynth(def.synth, this.ctx.sampleRate);
      this.buffers.set(id, baked);
      return baked;
    })();

    this.loading.set(id, p);
    void p.finally(() => this.loading.delete(id));
    return p;
  }

  /** Warm a batch of ids (e.g. critical SFX) before they're first needed. */
  async preload(ids: readonly string[]): Promise<void> {
    // Offline synth rendering/decode still competes for CPU with the live
    // mixer. Warm a few at a time instead of launching the entire bank at once.
    let next = 0;
    await Promise.all(Array.from({length: Math.min(3, ids.length)}, async () => {
      while (next < ids.length) await this.load(ids[next++]);
    }));
  }
}
